import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  FiBarChart2, FiSearch, FiDownload, FiPrinter,
  FiChevronDown, FiChevronRight, FiList, FiAlertCircle,
  FiCheckCircle, FiDollarSign
} from 'react-icons/fi';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';
import { colors } from '../../theme/colors';
import { formatNumber, formatDate as fmtDate } from '../../utils/formatters';
import useCurrency from '../../hooks/useCurrency';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { recordReads } from '../../utils/readTelemetry';
import { db } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import PageLayout from '../../components/PageLayout';
import { addPrintBrand } from '../../utils/printBranding';

// Account type display config
const ACCOUNT_TYPE_LABELS = {
  asset: 'Asset',
  liability: 'Liability',
  equity: 'Equity',
  revenue: 'Revenue',
  expense: 'Expense'
};

const ACCOUNT_TYPE_ORDER = ['asset', 'liability', 'equity', 'revenue', 'expense'];

const CHART_COLORS = [
  colors.chart.primary,
  colors.chart.secondary,
  colors.chart.tertiary,
  colors.chart.accent1,
  colors.chart.accent2,
  colors.chart.accent3
];

const formatAmount = (amount) => {
  const dp = (typeof window !== 'undefined' && Number.isFinite(Number(window.__AMAL_DECIMAL_PLACES__)))
    ? Number(window.__AMAL_DECIMAL_PLACES__) : 2;
  if (!amount && amount !== 0) return dp > 0 ? '0.' + '0'.repeat(dp) : '0';
  return Number(amount).toLocaleString('en-PK', {
    minimumFractionDigits: dp,
    maximumFractionDigits: dp
  });
};

const formatDate = (v) => fmtDate(v) || '-';

// Split a signed net (debit-positive) into a Debit/Credit amount + status label.
const splitDrCr = (net) => ({
  dr: net > 0.001 ? net : 0,
  cr: net < -0.001 ? -net : 0,
  status: net > 0.001 ? 'Debit' : net < -0.001 ? 'Credit' : '-'
});

// Normalise a FY startDate (string or Firestore timestamp) to YYYY-MM-DD.
const toISODate = (d) => {
  if (!d) return '';
  if (typeof d === 'string') return d.split('T')[0];
  if (d.toDate) { try { return d.toDate().toISOString().split('T')[0]; } catch { return ''; } }
  if (d.seconds) return new Date(d.seconds * 1000).toISOString().split('T')[0];
  try { return new Date(d).toISOString().split('T')[0]; } catch { return ''; }
};

const TrialBalance = () => {
  const { activeCompany, activeFinancialYear, activeBranch } = useAuth();
  const navigate = useNavigate();
  const { formatCurrency } = useCurrency();

  // State
  const [accounts, setAccounts] = useState([]);
  const [subTransactions, setSubTransactions] = useState([]);
  const [financialYears, setFinancialYears] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(false);

  // Filter state
  const [selectedFYId, setSelectedFYId] = useState('');
  const [asOfDate, setAsOfDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [accountTypeFilter, setAccountTypeFilter] = useState('all');
  const [accountLevelFilter, setAccountLevelFilter] = useState(5);
  const [searchQuery, setSearchQuery] = useState('');
  const [showZeroBalances, setShowZeroBalances] = useState(false);

  // View mode: 'hierarchy' (current tree) | 'two' (2-column flat) | 'six' (6-column with opening/movement/closing)
  const [viewMode, setViewMode] = useState('hierarchy');
  // From date — used by the 6-column view to split opening (before) vs movement (within range).
  const [fromDate, setFromDate] = useState('');

  // Account-code RANGE filter — applies at LEVEL 5 (postable accounts). Shows only
  // level-5 accounts whose code is within [fromAccount, toAccount]; parents roll up
  // only their in-range leaves. Blank = no bound on that end.
  const [fromAccount, setFromAccount] = useState('');
  const [toAccount, setToAccount] = useState('');

  // Expand/collapse state for hierarchy
  const [expandedNodes, setExpandedNodes] = useState({});

  // Load financial years
  const loadFinancialYears = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const fyRef = collection(db, 'companies', activeCompany.id, 'financialYears');
      const q = query(fyRef, orderBy('startDate', 'desc'));
      const snapshot = await getDocs(q);
      const fys = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setFinancialYears(fys);

      if (activeFinancialYear?.id) {
        setSelectedFYId(activeFinancialYear.id);
      } else if (fys.length > 0) {
        setSelectedFYId(fys[0].id);
      }
    } catch (error) {
      console.error('Error loading financial years:', error);
    }
  }, [activeCompany, activeFinancialYear]);

  // Load chart of accounts
  const loadAccounts = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const accountsRef = collection(db, 'companies', activeCompany.id, 'accounts');
      const q = query(accountsRef, orderBy('code', 'asc'));
      const snapshot = await getDocs(q);
      recordReads(activeCompany.id, snapshot.size);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error loading accounts:', error);
      return [];
    }
  }, [activeCompany]);

  // Load subTransactions for the selected FY
  const loadSubTransactions = useCallback(async () => {
    if (!activeCompany?.id || !selectedFYId) return [];
    try {
      const subTransRef = collection(db, 'companies', activeCompany.id, 'subTransactions');
      const q = query(
        subTransRef,
        where('status', 'in', ['Posted', 'Draft']),
        where('financialYearId', '==', selectedFYId)
      );
      const snapshot = await getDocs(q);
      recordReads(activeCompany.id, snapshot.size);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })).filter(t => {
        // Include Posted entries always, and Draft entries only from OB (Opening Balance) transactions
        if (t.status === 'Draft' && t.tranTypeCode !== 'OB') return false;
        if (!activeBranch || activeBranch.id === 'all') return true;
        return t.branchId === activeBranch.id;
      });
    } catch (error) {
      console.error('Error loading subTransactions:', error);
      return [];
    }
  }, [activeCompany, selectedFYId, activeBranch]);

  useEffect(() => {
    loadFinancialYears();
  }, [loadFinancialYears]);

  // Default the "From" date to the selected FY's start (for the 6-column view).
  useEffect(() => {
    const fy = financialYears.find(f => f.id === selectedFYId);
    if (fy) setFromDate(toISODate(fy.startDate));
  }, [selectedFYId, financialYears]);

  // Generate report
  const handleGenerateReport = async () => {
    if (!activeCompany?.id || !selectedFYId) return;

    setLoading(true);
    setHasGenerated(true);

    try {
      const [accs, trans] = await Promise.all([
        loadAccounts(),
        loadSubTransactions()
      ]);

      setAccounts(accs);
      setSubTransactions(trans);

      // Expand all parent nodes by default
      const expanded = {};
      accs.forEach(acc => {
        if (acc.level < 5) {
          expanded[acc.id] = true;
        }
      });
      setExpandedNodes(expanded);
    } catch (error) {
      console.error('Error generating trial balance:', error);
    } finally {
      setLoading(false);
    }
  };

  // Build account lookup map
  const accountMap = useMemo(() => {
    const map = {};
    accounts.forEach(acc => { map[acc.id] = acc; });
    return map;
  }, [accounts]);

  // Calculate balances per postable (level 5) account
  const accountBalances = useMemo(() => {
    const balances = {};

    // Filter transactions by asOfDate
    const filteredTrans = subTransactions.filter(t => {
      if (!asOfDate) return true;
      return (t.docDate || '') <= asOfDate;
    });

    // Aggregate debits and credits per accountId
    filteredTrans.forEach(t => {
      const accId = t.accountId;
      if (!balances[accId]) {
        balances[accId] = { totalDebit: 0, totalCredit: 0 };
      }
      balances[accId].totalDebit += (t.debitAmount || 0);
      balances[accId].totalCredit += (t.creditAmount || 0);
    });

    // Compute net debit/credit based on account type
    const result = {};
    Object.keys(balances).forEach(accId => {
      const acc = accountMap[accId];
      if (!acc) return;

      const { totalDebit, totalCredit } = balances[accId];
      const netAmount = totalDebit - totalCredit;
      const accountType = (acc.accountType || '').toLowerCase();

      let debitBalance = 0;
      let creditBalance = 0;

      if (['asset', 'expense'].includes(accountType)) {
        // Natural debit accounts
        if (netAmount >= 0) {
          debitBalance = netAmount;
        } else {
          creditBalance = Math.abs(netAmount);
        }
      } else {
        // Natural credit accounts (liability, equity, revenue)
        if (netAmount <= 0) {
          creditBalance = Math.abs(netAmount);
        } else {
          debitBalance = netAmount;
        }
      }

      result[accId] = {
        totalDebit,
        totalCredit,
        netAmount,
        debitBalance,
        creditBalance
      };
    });

    return result;
  }, [subTransactions, asOfDate, accountMap]);

  // ── Flat per-account data for the 2-column and 6-column views ──
  // opening = net before `fromDate`; period Dr/Cr = within [fromDate, asOfDate];
  // closing = opening + periodDr − periodCr (= all entries up to asOfDate).
  // Level-5 (postable) accounts, sorted by code — powers the range dropdowns.
  const level5Accounts = useMemo(
    () => accounts.filter(a => a.level === 5 || a.isPostable).sort((a, b) => (a.code || '').localeCompare(b.code || '')),
    [accounts]
  );

  const flatData = useMemo(() => {
    const acc5 = accounts.filter(a => a.level === 5 || a.isPostable);
    const byId = {};
    acc5.forEach(a => { byId[a.id] = { opening: 0, pDr: 0, pCr: 0 }; });
    const to = asOfDate || '';
    const from = fromDate || '';
    subTransactions.forEach(t => {
      const b = byId[t.accountId];
      if (!b) return;
      const d = t.docDate || '';
      if (to && d > to) return; // ignore after the as-of date
      const dr = Number(t.debitAmount) || 0;
      const cr = Number(t.creditAmount) || 0;
      if (from && d < from) b.opening += dr - cr;
      else { b.pDr += dr; b.pCr += cr; }
    });
    let rows = acc5.map(a => {
      const b = byId[a.id];
      const closingNet = b.opening + b.pDr - b.pCr;
      return {
        id: a.id, code: a.code, name: a.name, accountType: a.accountType,
        openingNet: b.opening, periodDr: b.pDr, periodCr: b.pCr, closingNet
      };
    });
    // Filters (account-code range / type / search / zero)
    if (fromAccount) rows = rows.filter(r => (r.code || '') >= fromAccount);
    if (toAccount) rows = rows.filter(r => (r.code || '') <= toAccount);
    if (accountTypeFilter !== 'all') rows = rows.filter(r => (r.accountType || '').toLowerCase() === accountTypeFilter);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      rows = rows.filter(r => (r.code || '').toLowerCase().includes(q) || (r.name || '').toLowerCase().includes(q));
    }
    if (!showZeroBalances) {
      rows = rows.filter(r => Math.abs(r.openingNet) > 0.001 || r.periodDr > 0.001 || r.periodCr > 0.001 || Math.abs(r.closingNet) > 0.001);
    }
    rows.sort((a, b) => (a.code || '').localeCompare(b.code || ''));
    return rows;
  }, [accounts, subTransactions, fromDate, asOfDate, accountTypeFilter, searchQuery, showZeroBalances, fromAccount, toAccount]);

  const flatTotals = useMemo(() => {
    const t = { openDr: 0, openCr: 0, perDr: 0, perCr: 0, closeDr: 0, closeCr: 0, openNet: 0, closeNet: 0 };
    flatData.forEach(r => {
      const o = splitDrCr(r.openingNet), c = splitDrCr(r.closingNet);
      t.openDr += o.dr; t.openCr += o.cr;
      t.perDr += r.periodDr; t.perCr += r.periodCr;
      t.closeDr += c.dr; t.closeCr += c.cr;
      t.openNet += r.openingNet; t.closeNet += r.closingNet;
    });
    return t;
  }, [flatData]);

  // Build parent-child hierarchy and roll up balances to parent levels
  const hierarchyData = useMemo(() => {
    if (accounts.length === 0) return { tree: [], flatRows: [], rollups: {} };

    // Build children map
    const childrenMap = {};
    accounts.forEach(acc => {
      const parentId = acc.parentId || null;
      if (!childrenMap[parentId]) childrenMap[parentId] = [];
      childrenMap[parentId].push(acc);
    });

    // Sort children by code
    Object.keys(childrenMap).forEach(key => {
      childrenMap[key].sort((a, b) => (a.code || '').localeCompare(b.code || ''));
    });

    // Roll up balances from level 5 to parents
    const rollups = {};

    const computeRollup = (accId) => {
      if (rollups[accId]) return rollups[accId];

      const acc = accountMap[accId];
      if (!acc) return { debitBalance: 0, creditBalance: 0 };

      // If it's a level 5 account with balances
      if (acc.level === 5 || acc.isPostable) {
        // Account-code range filter is applied at this (level-5) layer, so parents
        // above roll up only the in-range leaves.
        const code = acc.code || '';
        if ((fromAccount && code < fromAccount) || (toAccount && code > toAccount)) {
          rollups[accId] = { debitBalance: 0, creditBalance: 0 };
          return rollups[accId];
        }
        const bal = accountBalances[accId] || { debitBalance: 0, creditBalance: 0 };
        rollups[accId] = { debitBalance: bal.debitBalance, creditBalance: bal.creditBalance };
        return rollups[accId];
      }

      // Sum children
      const children = childrenMap[accId] || [];
      let totalDebit = 0;
      let totalCredit = 0;
      children.forEach(child => {
        const childRollup = computeRollup(child.id);
        totalDebit += childRollup.debitBalance;
        totalCredit += childRollup.creditBalance;
      });

      rollups[accId] = { debitBalance: totalDebit, creditBalance: totalCredit };
      return rollups[accId];
    };

    // Compute rollups for all accounts
    accounts.forEach(acc => computeRollup(acc.id));

    // Build tree roots (level 1 accounts with no parent or parentId === null/undefined)
    const roots = accounts.filter(acc => !acc.parentId || acc.level === 1);
    roots.sort((a, b) => (a.code || '').localeCompare(b.code || ''));

    // Flatten tree for display
    const flatRows = [];
    const buildFlatList = (accId, depth = 0) => {
      const acc = accountMap[accId];
      if (!acc) return;

      const bal = rollups[accId] || { debitBalance: 0, creditBalance: 0 };
      const children = childrenMap[accId] || [];
      const hasChildren = children.length > 0;
      const isLeaf = acc.level === 5 || acc.isPostable;

      flatRows.push({
        ...acc,
        depth,
        hasChildren,
        isLeaf,
        debitBalance: bal.debitBalance,
        creditBalance: bal.creditBalance
      });

      if (hasChildren) {
        children.forEach(child => buildFlatList(child.id, depth + 1));
      }
    };

    roots.forEach(root => buildFlatList(root.id, 0));

    return { tree: roots, flatRows, rollups, childrenMap };
  }, [accounts, accountBalances, accountMap, fromAccount, toAccount]);

  // Apply filters to flat rows
  const filteredRows = useMemo(() => {
    let rows = hierarchyData.flatRows;

    // Account-code range filter (level-5 leaves by code; parents kept only if they
    // still roll up a non-zero in-range balance).
    if (fromAccount || toAccount) {
      rows = rows.filter(r => {
        if (r.isLeaf) {
          const code = r.code || '';
          if (fromAccount && code < fromAccount) return false;
          if (toAccount && code > toAccount) return false;
          return true;
        }
        const bal = hierarchyData.rollups[r.id] || { debitBalance: 0, creditBalance: 0 };
        return Math.abs(bal.debitBalance) > 0.001 || Math.abs(bal.creditBalance) > 0.001;
      });
    }

    // Account type filter
    if (accountTypeFilter !== 'all') {
      rows = rows.filter(r => (r.accountType || '').toLowerCase() === accountTypeFilter);
    }

    // Account level filter - show accounts up to specified level
    rows = rows.filter(r => r.level <= accountLevelFilter);

    // Search filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      rows = rows.filter(r =>
        (r.code || '').toLowerCase().includes(q) ||
        (r.name || '').toLowerCase().includes(q)
      );
    }

    // Zero balances toggle
    if (!showZeroBalances) {
      rows = rows.filter(r => {
        const bal = hierarchyData.rollups[r.id] || { debitBalance: 0, creditBalance: 0 };
        return Math.abs(bal.debitBalance) > 0.001 || Math.abs(bal.creditBalance) > 0.001;
      });
    }

    return rows;
  }, [hierarchyData, accountTypeFilter, accountLevelFilter, searchQuery, showZeroBalances, fromAccount, toAccount]);

  // Summary calculations
  const summary = useMemo(() => {
    let totalDebit = 0;
    let totalCredit = 0;
    let accountCount = 0;

    // Only sum level 5 (postable) accounts to avoid double counting from rollups
    filteredRows.forEach(row => {
      if (row.level === 5 || row.isPostable) {
        const bal = accountBalances[row.id] || { debitBalance: 0, creditBalance: 0 };
        totalDebit += bal.debitBalance;
        totalCredit += bal.creditBalance;
        accountCount++;
      }
    });

    // If filtered to a level less than 5, count top-level rows as the account count
    if (accountLevelFilter < 5) {
      accountCount = filteredRows.filter(r => r.level === accountLevelFilter).length;
      // Recalculate from rollups at the filtered level
      totalDebit = 0;
      totalCredit = 0;
      filteredRows.forEach(row => {
        if (row.level === accountLevelFilter) {
          const bal = hierarchyData.rollups[row.id] || { debitBalance: 0, creditBalance: 0 };
          totalDebit += bal.debitBalance;
          totalCredit += bal.creditBalance;
        }
      });
    }

    const difference = Math.abs(totalDebit - totalCredit);

    return { totalAccounts: accountCount, totalDebit, totalCredit, difference };
  }, [filteredRows, accountBalances, accountLevelFilter, hierarchyData.rollups]);

  // Chart data: Debit vs Credit by account type
  const barChartData = useMemo(() => {
    const typeData = {};
    ACCOUNT_TYPE_ORDER.forEach(t => {
      typeData[t] = { type: ACCOUNT_TYPE_LABELS[t], debit: 0, credit: 0 };
    });

    // Sum from level 5 accounts only to avoid double-counting
    accounts.forEach(acc => {
      if (acc.level !== 5 && !acc.isPostable) return;
      const accountType = (acc.accountType || '').toLowerCase();
      if (!typeData[accountType]) return;
      const bal = accountBalances[acc.id];
      if (!bal) return;
      typeData[accountType].debit += bal.debitBalance;
      typeData[accountType].credit += bal.creditBalance;
    });

    return Object.values(typeData);
  }, [accounts, accountBalances]);

  // Pie chart data: Distribution by account type (absolute balances)
  const pieChartData = useMemo(() => {
    const typeData = {};
    ACCOUNT_TYPE_ORDER.forEach(t => {
      typeData[t] = { name: ACCOUNT_TYPE_LABELS[t], value: 0 };
    });

    accounts.forEach(acc => {
      if (acc.level !== 5 && !acc.isPostable) return;
      const accountType = (acc.accountType || '').toLowerCase();
      if (!typeData[accountType]) return;
      const bal = accountBalances[acc.id];
      if (!bal) return;
      typeData[accountType].value += bal.debitBalance + bal.creditBalance;
    });

    return Object.values(typeData).filter(d => d.value > 0);
  }, [accounts, accountBalances]);

  // Toggle hierarchy expansion
  const toggleNode = (accId) => {
    setExpandedNodes(prev => ({ ...prev, [accId]: !prev[accId] }));
  };

  const expandAll = () => {
    const expanded = {};
    accounts.forEach(acc => {
      if (acc.level < 5) expanded[acc.id] = true;
    });
    setExpandedNodes(expanded);
  };

  const collapseAll = () => setExpandedNodes({});

  // Determine visible rows considering expand/collapse
  const visibleRows = useMemo(() => {
    if (searchQuery.trim() || accountTypeFilter !== 'all') {
      // When filtering, show flat list
      return filteredRows;
    }

    // With hierarchy, check parent expansion state
    const visible = [];
    const parentStack = []; // Track which parent levels are expanded

    filteredRows.forEach(row => {
      // Level 1 (roots) always visible
      if (row.depth === 0 || row.level === 1) {
        visible.push(row);
        return;
      }

      // Check if all ancestor nodes are expanded
      let allAncestorsExpanded = true;
      // Find parent account
      let currentParentId = row.parentId;
      while (currentParentId) {
        if (!expandedNodes[currentParentId]) {
          allAncestorsExpanded = false;
          break;
        }
        const parentAcc = accountMap[currentParentId];
        currentParentId = parentAcc?.parentId || null;
      }

      if (allAncestorsExpanded) {
        visible.push(row);
      }
    });

    return visible;
  }, [filteredRows, expandedNodes, searchQuery, accountTypeFilter, accountMap]);

  // CSV Export
  const handleExport = () => {
    if (visibleRows.length === 0) return;

    const selectedFY = financialYears.find(f => f.id === selectedFYId);

    // Export mirrors the view on screen (Hierarchy / 2-Column / 6-Column) —
    // it used to always export the hierarchy regardless of the selected view.
    const isSix = viewMode === 'six';
    const isTwo = viewMode === 'two';

    let csv = 'Trial Balance Report\n';
    csv += `Company: ${activeCompany?.companyName || ''}\n`;
    csv += `Financial Year: ${selectedFY?.yearName || selectedFY?.yearCode || ''}\n`;
    csv += isSix
      ? `From Date: ${fromDate ? formatDate(fromDate) : '(start)'}\nTo Date: ${formatDate(asOfDate)}\n`
      : `As Of Date: ${formatDate(asOfDate)}\n`;
    csv += `View: ${isSix ? '6-Column' : isTwo ? '2-Column' : 'Hierarchy'}\n`;
    csv += `Generated: ${formatDate(new Date().toISOString().split('T')[0])}\n\n`;

    if (isSix) {
      csv += 'Account Code,Account Name,Opening,Opening Status,Debit,Credit,Closing,Closing Status\n';
      flatData.forEach(r => {
        const o = splitDrCr(r.openingNet);
        const c = splitDrCr(r.closingNet);
        const oAmt = o.dr || o.cr;
        const cAmt = c.dr || c.cr;
        csv += `"${r.code || ''}","${(r.name || '').replace(/"/g, '""')}",`;
        csv += `${oAmt ? oAmt.toFixed(2) : ''},${o.status},`;
        csv += `${r.periodDr ? r.periodDr.toFixed(2) : ''},${r.periodCr ? r.periodCr.toFixed(2) : ''},`;
        csv += `${cAmt ? cAmt.toFixed(2) : ''},${c.status}\n`;
      });
      csv += `\n,Grand Total,${Math.abs(flatTotals.openNet).toFixed(2)},${splitDrCr(flatTotals.openNet).status},`;
      csv += `${flatTotals.perDr.toFixed(2)},${flatTotals.perCr.toFixed(2)},`;
      csv += `${Math.abs(flatTotals.closeNet).toFixed(2)},${splitDrCr(flatTotals.closeNet).status}\n`;
      csv += `\nTotal Accounts:,${flatData.length}\n`;
    } else if (isTwo) {
      csv += 'Account Code,Account Name,Debit,Credit\n';
      flatData.forEach(r => {
        const c = splitDrCr(r.closingNet);
        csv += `"${r.code || ''}","${(r.name || '').replace(/"/g, '""')}",`;
        csv += `${c.dr > 0 ? c.dr.toFixed(2) : ''},${c.cr > 0 ? c.cr.toFixed(2) : ''}\n`;
      });
      csv += `\n,Grand Total,${flatTotals.closeDr.toFixed(2)},${flatTotals.closeCr.toFixed(2)}\n`;
      csv += `\nTotal Accounts:,${flatData.length}\n`;
      csv += `Difference:,${Math.abs(flatTotals.closeDr - flatTotals.closeCr).toFixed(2)}\n`;
    } else {
      csv += 'Account Code,Account Name,Level,Account Type,Debit,Credit\n';
      visibleRows.forEach(row => {
        const bal = hierarchyData.rollups[row.id] || { debitBalance: 0, creditBalance: 0 };
        const indent = '  '.repeat(row.depth || 0);
        csv += `"${row.code || ''}","${indent}${(row.name || '').replace(/"/g, '""')}",`;
        csv += `${row.level || ''},${ACCOUNT_TYPE_LABELS[(row.accountType || '').toLowerCase()] || ''},`;
        csv += `${bal.debitBalance > 0 ? bal.debitBalance.toFixed(2) : ''},`;
        csv += `${bal.creditBalance > 0 ? bal.creditBalance.toFixed(2) : ''}\n`;
      });
      csv += `\n,,,,${summary.totalDebit.toFixed(2)},${summary.totalCredit.toFixed(2)}\n`;
      csv += `\nTotal Accounts:,${summary.totalAccounts}\n`;
      csv += `Difference:,${summary.difference.toFixed(2)}\n`;
    }

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Trial_Balance_${asOfDate || 'all'}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Print — MUST mirror the view on screen (Hierarchy / 2-Column / 6-Column).
  // It previously always rendered the hierarchy regardless of the selected view.
  const handlePrint = () => {
    const selectedFY = financialYears.find(f => f.id === selectedFYId);
    const isSix = viewMode === 'six';
    const isTwo = viewMode === 'two';
    const viewLabel = isSix ? '6-Column' : isTwo ? '2-Column' : 'Hierarchy';

    // Per-view summary so the header block agrees with the table under it. The
    // flat views total from flatTotals (level-5 rows); hierarchy uses `summary`
    // (which already avoids double-counting rollups).
    const sumDebit = (isTwo || isSix) ? flatTotals.closeDr : summary.totalDebit;
    const sumCredit = (isTwo || isSix) ? flatTotals.closeCr : summary.totalCredit;
    const sumAccounts = (isTwo || isSix) ? flatData.length : summary.totalAccounts;
    const sumDiff = Math.abs(Math.round((sumDebit - sumCredit) * 100) / 100);

    const dateMeta = isSix
      ? `From: ${fromDate ? formatDate(fromDate) : '(start)'} | To: ${formatDate(asOfDate)}`
      : `As Of Date: ${formatDate(asOfDate)}`;

    let headHtml = '';
    let bodyHtml = '';
    if (isSix) {
      headHtml = `<tr>
        <th>Code</th><th>Account Name</th>
        <th class="amount">Opening</th><th class="ctr">Status</th>
        <th class="amount">Debit</th><th class="amount">Credit</th>
        <th class="amount">Closing</th><th class="ctr">Status</th>
      </tr>`;
      bodyHtml = flatData.map(r => {
        const o = splitDrCr(r.openingNet);
        const c = splitDrCr(r.closingNet);
        const oAmt = o.dr || o.cr;
        const cAmt = c.dr || c.cr;
        return `<tr>
          <td>${r.code || ''}</td>
          <td>${r.name || ''}</td>
          <td class="amount">${oAmt > 0.001 ? formatAmount(oAmt) : '-'}</td>
          <td class="ctr">${o.status}</td>
          <td class="amount">${r.periodDr > 0.001 ? formatAmount(r.periodDr) : '-'}</td>
          <td class="amount">${r.periodCr > 0.001 ? formatAmount(r.periodCr) : '-'}</td>
          <td class="amount">${cAmt > 0.001 ? formatAmount(cAmt) : '-'}</td>
          <td class="ctr">${c.status}</td>
        </tr>`;
      }).join('') + `<tr class="total-row">
        <td colspan="2" style="text-align:right">Grand Total</td>
        <td class="amount">${formatAmount(Math.abs(flatTotals.openNet))}</td>
        <td class="ctr">${splitDrCr(flatTotals.openNet).status}</td>
        <td class="amount">${formatAmount(flatTotals.perDr)}</td>
        <td class="amount">${formatAmount(flatTotals.perCr)}</td>
        <td class="amount">${formatAmount(Math.abs(flatTotals.closeNet))}</td>
        <td class="ctr">${splitDrCr(flatTotals.closeNet).status}</td>
      </tr>`;
    } else if (isTwo) {
      headHtml = `<tr>
        <th>Account Code</th><th>Account Name</th>
        <th class="amount">Debit</th><th class="amount">Credit</th>
      </tr>`;
      bodyHtml = flatData.map(r => {
        const c = splitDrCr(r.closingNet);
        return `<tr>
          <td>${r.code || ''}</td>
          <td>${r.name || ''}</td>
          <td class="amount">${c.dr > 0.001 ? formatAmount(c.dr) : '-'}</td>
          <td class="amount">${c.cr > 0.001 ? formatAmount(c.cr) : '-'}</td>
        </tr>`;
      }).join('') + `<tr class="total-row">
        <td colspan="2" style="text-align:right">Grand Total</td>
        <td class="amount">${formatAmount(flatTotals.closeDr)}</td>
        <td class="amount">${formatAmount(flatTotals.closeCr)}</td>
      </tr>`;
    } else {
      headHtml = `<tr>
        <th>Account Code</th><th>Account Name</th>
        <th class="amount">Debit</th><th class="amount">Credit</th>
      </tr>`;
      bodyHtml = visibleRows.map(row => {
        const bal = hierarchyData.rollups[row.id] || { debitBalance: 0, creditBalance: 0 };
        const isParent = row.hasChildren && !row.isLeaf;
        const levelClass = `level-${Math.min(row.level || 1, 5)}`;
        return `<tr class="${isParent ? 'parent-row' : ''} ${levelClass}">
          <td>${row.code || ''}</td>
          <td class="${levelClass}">${row.name || ''}</td>
          <td class="amount">${bal.debitBalance > 0.001 ? formatAmount(bal.debitBalance) : '-'}</td>
          <td class="amount">${bal.creditBalance > 0.001 ? formatAmount(bal.creditBalance) : '-'}</td>
        </tr>`;
      }).join('') + `<tr class="total-row">
        <td colspan="2" style="text-align:right">Grand Total</td>
        <td class="amount">${formatAmount(summary.totalDebit)}</td>
        <td class="amount">${formatAmount(summary.totalCredit)}</td>
      </tr>`;
    }

    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>Trial Balance</title>
      <style>
        body{font-family:'Segoe UI',sans-serif;margin:30px;color:#1A1A1A;font-size:13px}
        h1{color:#800020;font-size:22px;margin-bottom:5px}
        .meta{color:#666;font-size:12px;margin-bottom:20px}
        .summary{display:flex;gap:30px;margin-bottom:20px;padding:15px;background:#f9f9f9;border-radius:8px}
        .summary-item{text-align:center}
        .summary-label{font-size:11px;color:#666;margin-bottom:4px}
        .summary-value{font-size:16px;font-weight:600}
        table{width:100%;border-collapse:collapse;margin-bottom:15px}
        th{background:#800020;color:white;padding:8px 12px;text-align:left;font-size:11px;text-transform:uppercase}
        th.amount{text-align:right}
        td{padding:6px 12px;border-bottom:1px solid #ddd;font-size:12px}
        td.amount{text-align:right;font-family:'Consolas',monospace}
        th.ctr,td.ctr{text-align:center;font-size:11px}
        .parent-row{background:#f5f5f5;font-weight:600}
        .total-row{background:#800020;color:white;font-weight:700;font-size:13px}
        .total-row td{border-bottom:none;padding:10px 12px}
        .level-1{font-weight:700;font-size:13px}
        .level-2{padding-left:20px}
        .level-3{padding-left:35px}
        .level-4{padding-left:50px}
        .level-5{padding-left:65px}
        .balanced{color:#28A745} .unbalanced{color:#DC3545}
        @media print{body{margin:15px}}
        /* The 6-column view is wide — print it landscape so nothing is cut off. */
        @page{size:${isSix ? 'landscape' : 'portrait'}}
      </style></head><body>
      <h1>Trial Balance</h1>
      <div class="meta">
        ${activeCompany?.companyName || 'Company'} |
        Financial Year: ${selectedFY?.yearName || selectedFY?.yearCode || ''} |
        ${dateMeta} |
        View: ${viewLabel} |
        Generated: ${formatDate(new Date().toISOString().split('T')[0])}
      </div>
      <div class="summary">
        <div class="summary-item">
          <div class="summary-label">Total Accounts</div>
          <div class="summary-value">${sumAccounts}</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">Total Debit</div>
          <div class="summary-value">${formatAmount(sumDebit)}</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">Total Credit</div>
          <div class="summary-value">${formatAmount(sumCredit)}</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">Difference</div>
          <div class="summary-value ${sumDiff < 0.01 ? 'balanced' : 'unbalanced'}">
            ${sumDiff < 0.01 ? 'Balanced' : formatAmount(sumDiff)}
          </div>
        </div>
      </div>
      <table>
        <thead>${headHtml}</thead>
        <tbody>${bodyHtml}</tbody>
      </table>
    </body></html>`);
    addPrintBrand(w);
    w.document.close();
    w.print();
  };

  // Custom tooltip for bar chart
  const CustomBarTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <TooltipBox>
          <TooltipTitle>{label}</TooltipTitle>
          {payload.map((p, i) => (
            <TooltipRow key={i}>
              <TooltipDot style={{ background: p.color }} />
              <span>{p.name}: {formatAmount(p.value)}</span>
            </TooltipRow>
          ))}
        </TooltipBox>
      );
    }
    return null;
  };

  // Custom tooltip for pie chart
  const CustomPieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      return (
        <TooltipBox>
          <TooltipTitle>{data.name}</TooltipTitle>
          <TooltipRow>
            <span>Balance: {formatAmount(data.value)}</span>
          </TooltipRow>
        </TooltipBox>
      );
    }
    return null;
  };

  return (
    <PageLayout pageTitle="Trial Balance" breadcrumb="Reports / Accounting Reports / Trial Balance">
      {/* Filters */}
      <FiltersCard>
        <FiltersRow>
          <FilterGroup>
            <Label>Financial Year</Label>
            <Select value={selectedFYId} onChange={e => setSelectedFYId(e.target.value)}>
              <option value="">Select Financial Year</option>
              {financialYears.map(fy => (
                <option key={fy.id} value={fy.id}>
                  {fy.yearName || fy.yearCode}
                  {fy.isActive ? ' (Active)' : ''}
                </option>
              ))}
            </Select>
          </FilterGroup>
          <FilterGroup style={{ minWidth: 270 }}>
            <Label>View</Label>
            <ToggleRow>
              <ToggleBtn $active={viewMode === 'hierarchy'} onClick={() => setViewMode('hierarchy')}>Hierarchy</ToggleBtn>
              <ToggleBtn $active={viewMode === 'two'} onClick={() => setViewMode('two')}>2-Column</ToggleBtn>
              <ToggleBtn $active={viewMode === 'six'} onClick={() => setViewMode('six')}>6-Column</ToggleBtn>
            </ToggleRow>
          </FilterGroup>
          {viewMode === 'six' && (
            <FilterGroup>
              <Label>From Date</Label>
              <Input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} />
            </FilterGroup>
          )}
          <FilterGroup>
            <Label>{viewMode === 'six' ? 'To Date' : 'As Of Date'}</Label>
            <Input
              type="date"
              value={asOfDate}
              onChange={e => setAsOfDate(e.target.value)}
            />
          </FilterGroup>
          <FilterGroup style={{ minWidth: 200 }}>
            <Label>From Account (L5)</Label>
            <Select value={fromAccount} onChange={e => setFromAccount(e.target.value)}>
              <option value="">-- First --</option>
              {level5Accounts.map(a => (
                <option key={a.id} value={a.code}>{a.code} - {a.name}</option>
              ))}
            </Select>
          </FilterGroup>
          <FilterGroup style={{ minWidth: 200 }}>
            <Label>To Account (L5)</Label>
            <Select value={toAccount} onChange={e => setToAccount(e.target.value)}>
              <option value="">-- Last --</option>
              {level5Accounts.map(a => (
                <option key={a.id} value={a.code}>{a.code} - {a.name}</option>
              ))}
            </Select>
          </FilterGroup>
          <FilterGroup>
            <Label>Account Type</Label>
            <Select value={accountTypeFilter} onChange={e => setAccountTypeFilter(e.target.value)}>
              <option value="all">All Types</option>
              {ACCOUNT_TYPE_ORDER.map(t => (
                <option key={t} value={t}>{ACCOUNT_TYPE_LABELS[t]}</option>
              ))}
            </Select>
          </FilterGroup>
          <FilterGroup>
            <Label>Account Level</Label>
            <Select
              value={accountLevelFilter}
              onChange={e => setAccountLevelFilter(Number(e.target.value))}
            >
              <option value={1}>Level 1 - Top Level</option>
              <option value={2}>Level 2</option>
              <option value={3}>Level 3</option>
              <option value={4}>Level 4</option>
              <option value={5}>Level 5 - Detail</option>
            </Select>
          </FilterGroup>
        </FiltersRow>
        <FiltersRow style={{ marginTop: 14 }}>
          <SearchWrap>
            <SearchIcon><FiSearch /></SearchIcon>
            <SearchInput
              type="text"
              placeholder="Search by account code or name..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
          </SearchWrap>
          <CheckboxGroup>
            <Checkbox
              type="checkbox"
              id="showZero"
              checked={showZeroBalances}
              onChange={e => setShowZeroBalances(e.target.checked)}
            />
            <CheckboxLabel htmlFor="showZero">Show Zero Balances</CheckboxLabel>
          </CheckboxGroup>
          <ButtonGroup>
            <Button onClick={expandAll} disabled={!hasGenerated} title="Expand All">
              <FiChevronDown /> Expand
            </Button>
            <Button onClick={collapseAll} disabled={!hasGenerated} title="Collapse All">
              <FiChevronRight /> Collapse
            </Button>
            <Button onClick={handleExport} disabled={visibleRows.length === 0}>
              <FiDownload /> CSV
            </Button>
            <Button onClick={handlePrint} disabled={visibleRows.length === 0}>
              <FiPrinter /> Print
            </Button>
            <Button primary onClick={handleGenerateReport} disabled={loading || !selectedFYId}>
              <FiBarChart2 /> {loading ? 'Loading...' : 'Generate'}
            </Button>
          </ButtonGroup>
        </FiltersRow>
      </FiltersCard>

      {/* Summary Cards */}
      {hasGenerated && !loading && (
        <SummaryCards>
          <SummaryCard>
            <SummaryIcon color={colors.primary.main}><FiList /></SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Total Accounts</SummaryLabel>
              <SummaryValue>{formatNumber(summary.totalAccounts)}</SummaryValue>
            </SummaryInfo>
          </SummaryCard>
          <SummaryCard>
            <SummaryIcon color="#1565C0">
              <span style={{ fontSize: 16, fontWeight: 700 }}>Dr</span>
            </SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Total Debit Balances</SummaryLabel>
              <SummaryValue style={{ color: '#1565C0' }}>
                {formatCurrency(summary.totalDebit)}
              </SummaryValue>
            </SummaryInfo>
          </SummaryCard>
          <SummaryCard>
            <SummaryIcon color="#C62828">
              <span style={{ fontSize: 16, fontWeight: 700 }}>Cr</span>
            </SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Total Credit Balances</SummaryLabel>
              <SummaryValue style={{ color: '#C62828' }}>
                {formatCurrency(summary.totalCredit)}
              </SummaryValue>
            </SummaryInfo>
          </SummaryCard>
          <SummaryCard>
            <SummaryIcon color={summary.difference < 0.01 ? colors.success.main : colors.error.main}>
              {summary.difference < 0.01 ? <FiCheckCircle /> : <FiAlertCircle />}
            </SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Difference</SummaryLabel>
              <SummaryValue style={{ color: summary.difference < 0.01 ? colors.success.main : colors.error.main }}>
                {summary.difference < 0.01 ? 'Balanced' : formatCurrency(summary.difference)}
              </SummaryValue>
            </SummaryInfo>
          </SummaryCard>
        </SummaryCards>
      )}

      {/* Table */}
      <ContentCard>
        <TableContainer>
          {loading ? (
            <EmptyState>
              <EmptyIcon><FiBarChart2 /></EmptyIcon>
              <EmptyText>Generating trial balance...</EmptyText>
            </EmptyState>
          ) : !hasGenerated ? (
            <EmptyState>
              <EmptyIcon><FiBarChart2 /></EmptyIcon>
              <EmptyText>Select a financial year and click Generate to view the trial balance</EmptyText>
            </EmptyState>
          ) : (viewMode === 'hierarchy' ? visibleRows.length === 0 : flatData.length === 0) ? (
            <EmptyState>
              <EmptyIcon><FiDollarSign /></EmptyIcon>
              <EmptyText>No accounts with balances found for the selected criteria</EmptyText>
              <p style={{ fontSize: '14px', color: colors.text.tertiary, marginTop: 8 }}>
                Try adjusting your filters or enabling "Show Zero Balances"
              </p>
            </EmptyState>
          ) : viewMode === 'two' ? (
            <Table>
              <Thead>
                <Tr>
                  <Th style={{ width: 150 }}>Account Code</Th>
                  <Th>Account Name</Th>
                  <Th align="right" style={{ width: 180 }}>Debit</Th>
                  <Th align="right" style={{ width: 180 }}>Credit</Th>
                </Tr>
              </Thead>
              <Tbody>
                {flatData.map(r => {
                  const c = splitDrCr(r.closingNet);
                  return (
                    <Tr key={r.id}>
                      <Td><AccountCode $level={5}>{r.code}</AccountCode></Td>
                      <Td>
                        <ClickableAccountName onClick={() => navigate('/reports/ledger', { state: { filterAccountId: r.id, filterAccountName: r.name } })}>
                          {r.name}
                        </ClickableAccountName>
                      </Td>
                      <AmountCell>{c.dr > 0.001 ? formatAmount(c.dr) : '-'}</AmountCell>
                      <AmountCell>{c.cr > 0.001 ? formatAmount(c.cr) : '-'}</AmountCell>
                    </Tr>
                  );
                })}
              </Tbody>
              <Tfoot>
                <TotalRow>
                  <Td colSpan={2} style={{ textAlign: 'right', fontWeight: 700, fontSize: 14 }}>Grand Total</Td>
                  <AmountCell $isTotal>{formatAmount(flatTotals.closeDr)}</AmountCell>
                  <AmountCell $isTotal>{formatAmount(flatTotals.closeCr)}</AmountCell>
                </TotalRow>
              </Tfoot>
            </Table>
          ) : viewMode === 'six' ? (
            <Table>
              <Thead>
                <Tr>
                  <Th style={{ width: 130 }}>Code</Th>
                  <Th>Account Name</Th>
                  <Th align="right">Opening</Th>
                  <Th align="center" style={{ width: 70 }}>Status</Th>
                  <Th align="right">Debit</Th>
                  <Th align="right">Credit</Th>
                  <Th align="right">Closing</Th>
                  <Th align="center" style={{ width: 70 }}>Status</Th>
                </Tr>
              </Thead>
              <Tbody>
                {flatData.map(r => {
                  const o = splitDrCr(r.openingNet);
                  const c = splitDrCr(r.closingNet);
                  const oAmt = o.dr || o.cr;
                  const cAmt = c.dr || c.cr;
                  return (
                    <Tr key={r.id}>
                      <Td><AccountCode $level={5}>{r.code}</AccountCode></Td>
                      <Td>
                        <ClickableAccountName onClick={() => navigate('/reports/ledger', { state: { filterAccountId: r.id, filterAccountName: r.name } })}>
                          {r.name}
                        </ClickableAccountName>
                      </Td>
                      <AmountCell>{oAmt > 0.001 ? formatAmount(oAmt) : '-'}</AmountCell>
                      <Td align="center"><StatusPill $cr={o.status === 'Credit'}>{o.status}</StatusPill></Td>
                      <AmountCell>{r.periodDr > 0.001 ? formatAmount(r.periodDr) : '-'}</AmountCell>
                      <AmountCell>{r.periodCr > 0.001 ? formatAmount(r.periodCr) : '-'}</AmountCell>
                      <AmountCell>{cAmt > 0.001 ? formatAmount(cAmt) : '-'}</AmountCell>
                      <Td align="center"><StatusPill $cr={c.status === 'Credit'}>{c.status}</StatusPill></Td>
                    </Tr>
                  );
                })}
              </Tbody>
              <Tfoot>
                <TotalRow>
                  <Td colSpan={2} style={{ textAlign: 'right', fontWeight: 700, fontSize: 14 }}>Grand Total</Td>
                  <AmountCell $isTotal>{formatAmount(Math.abs(flatTotals.openNet))}</AmountCell>
                  <Td align="center" style={{ fontWeight: 700 }}>{splitDrCr(flatTotals.openNet).status}</Td>
                  <AmountCell $isTotal>{formatAmount(flatTotals.perDr)}</AmountCell>
                  <AmountCell $isTotal>{formatAmount(flatTotals.perCr)}</AmountCell>
                  <AmountCell $isTotal>{formatAmount(Math.abs(flatTotals.closeNet))}</AmountCell>
                  <Td align="center" style={{ fontWeight: 700 }}>{splitDrCr(flatTotals.closeNet).status}</Td>
                </TotalRow>
              </Tfoot>
            </Table>
          ) : (
            <Table>
              <Thead>
                <Tr>
                  <Th style={{ width: 30 }}></Th>
                  <Th style={{ width: 140 }}>Account Code</Th>
                  <Th>Account Name</Th>
                  <Th align="right" style={{ width: 160 }}>Debit</Th>
                  <Th align="right" style={{ width: 160 }}>Credit</Th>
                </Tr>
              </Thead>
              <Tbody>
                {visibleRows.map((row, index) => {
                  const bal = hierarchyData.rollups[row.id] || { debitBalance: 0, creditBalance: 0 };
                  const isParent = row.hasChildren && !row.isLeaf;
                  const isExpanded = expandedNodes[row.id];
                  const level = row.level || 1;

                  return (
                    <Tr
                      key={row.id}
                      $isParent={isParent}
                      $level={level}
                      onClick={isParent ? () => toggleNode(row.id) : undefined}
                      style={{ cursor: isParent ? 'pointer' : 'default' }}
                    >
                      <Td style={{ textAlign: 'center', padding: '8px 4px', width: 30 }}>
                        {isParent ? (
                          isExpanded ?
                            <FiChevronDown size={14} style={{ color: colors.text.secondary }} /> :
                            <FiChevronRight size={14} style={{ color: colors.text.secondary }} />
                        ) : null}
                      </Td>
                      <Td>
                        <AccountCode $level={level}>{row.code}</AccountCode>
                      </Td>
                      <Td>
                        <AccountName $level={level} $indent={(level - 1) * 16}>
                          {!isParent ? (
                            <ClickableAccountName
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate('/reports/ledger', {
                                  state: { filterAccountId: row.id, filterAccountName: row.name }
                                });
                              }}
                            >
                              {row.name}
                            </ClickableAccountName>
                          ) : (
                            row.name
                          )}
                          {isParent && (
                            <AccountTypeBadge>{ACCOUNT_TYPE_LABELS[(row.accountType || '').toLowerCase()] || ''}</AccountTypeBadge>
                          )}
                        </AccountName>
                      </Td>
                      <AmountCell align="right" $isParent={isParent}>
                        {bal.debitBalance > 0.001 ? formatAmount(bal.debitBalance) : '-'}
                      </AmountCell>
                      <AmountCell align="right" $isParent={isParent}>
                        {bal.creditBalance > 0.001 ? formatAmount(bal.creditBalance) : '-'}
                      </AmountCell>
                    </Tr>
                  );
                })}
              </Tbody>
              <Tfoot>
                <TotalRow>
                  <Td colSpan={3} style={{ textAlign: 'right', fontWeight: 700, fontSize: 14 }}>
                    Grand Total
                  </Td>
                  <AmountCell align="right" $isTotal>
                    {formatAmount(summary.totalDebit)}
                  </AmountCell>
                  <AmountCell align="right" $isTotal>
                    {formatAmount(summary.totalCredit)}
                  </AmountCell>
                </TotalRow>
                {summary.difference >= 0.01 && (
                  <DifferenceRow>
                    <Td colSpan={3} style={{ textAlign: 'right', fontWeight: 600, fontSize: 13 }}>
                      Difference (Out of Balance)
                    </Td>
                    <AmountCell align="right" colSpan={2} style={{ color: colors.error.main, fontWeight: 700 }}>
                      {formatAmount(summary.difference)}
                    </AmountCell>
                  </DifferenceRow>
                )}
              </Tfoot>
            </Table>
          )}
        </TableContainer>
      </ContentCard>

      {/* Charts */}
      {hasGenerated && !loading && (barChartData.some(d => d.debit > 0 || d.credit > 0)) && (
        <ChartsRow>
          <ChartCard>
            <ChartTitle>Debit vs Credit by Account Type</ChartTitle>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={barChartData} margin={{ top: 10, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={colors.neutral[200]} />
                <XAxis dataKey="type" fontSize={12} tick={{ fill: colors.text.secondary }} />
                <YAxis fontSize={11} tick={{ fill: colors.text.secondary }} tickFormatter={(v) => {
                  if (v >= 10000000) return `${(v / 10000000).toFixed(1)}Cr`;
                  if (v >= 100000) return `${(v / 100000).toFixed(1)}L`;
                  if (v >= 1000) return `${(v / 1000).toFixed(0)}K`;
                  return v;
                }} />
                <Tooltip content={<CustomBarTooltip />} />
                <Legend />
                <Bar dataKey="debit" name="Debit" fill="#1565C0" radius={[4, 4, 0, 0]} />
                <Bar dataKey="credit" name="Credit" fill="#C62828" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
          <ChartCard>
            <ChartTitle>Balance Distribution by Type</ChartTitle>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={pieChartData}
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  innerRadius={50}
                  paddingAngle={2}
                  dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  labelLine={true}
                  fontSize={11}
                >
                  {pieChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip content={<CustomPieTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>
        </ChartsRow>
      )}
    </PageLayout>
  );
};

// ============ STYLED COMPONENTS ============

const FiltersCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
  margin-bottom: 20px;
`;

const FiltersRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  align-items: flex-end;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 150px;
`;

const Label = styled.label`
  font-size: 11px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const Select = styled.select`
  padding: 9px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  color: ${colors.text.primary};
  background: white;
  cursor: pointer;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }
`;

const Input = styled.input`
  padding: 9px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  color: ${colors.text.primary};
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }
`;

const SearchWrap = styled.div`
  position: relative;
  flex: 1;
  min-width: 220px;
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: ${colors.text.secondary};
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 9px 12px 9px 38px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  color: ${colors.text.primary};
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }

  &::placeholder {
    color: ${colors.text.tertiary};
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 0;
`;

const Checkbox = styled.input`
  width: 16px;
  height: 16px;
  accent-color: ${colors.primary.main};
  cursor: pointer;
`;

const CheckboxLabel = styled.label`
  font-size: 13px;
  color: ${colors.text.primary};
  cursor: pointer;
  white-space: nowrap;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
`;

const Button = styled.button`
  padding: 9px 14px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;
  white-space: nowrap;

  ${props => props.primary ? `
    background: ${colors.primary.main};
    color: white;
    border-color: ${colors.primary.main};
    &:hover:not(:disabled) {
      background: ${colors.primary.dark};
      border-color: ${colors.primary.dark};
    }
  ` : `
    background: white;
    color: ${colors.text.primary};
    &:hover:not(:disabled) {
      background: ${colors.neutral[100]};
      border-color: ${colors.primary.main};
    }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ToggleRow = styled.div`
  display: flex;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  overflow: hidden;
`;

const ToggleBtn = styled.button`
  flex: 1;
  padding: 9px 10px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  white-space: nowrap;
  background: ${props => props.$active ? colors.primary.main : 'white'};
  color: ${props => props.$active ? 'white' : colors.text.secondary};
  & + & { border-left: 1px solid ${colors.neutral[300]}; }
  &:hover { background: ${props => props.$active ? colors.primary.dark : colors.neutral[100]}; }
`;

const StatusPill = styled.span`
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
  background: ${props => props.$cr ? '#C6282815' : '#1565C015'};
  color: ${props => props.$cr ? '#C62828' : '#1565C0'};
`;

const SummaryCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
`;

const SummaryCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const SummaryIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${colors.borderRadius.md};
  background: ${props => props.color || colors.primary.main}15;
  color: ${props => props.color || colors.primary.main};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
`;

const SummaryInfo = styled.div`
  min-width: 0;
`;

const SummaryLabel = styled.div`
  font-size: 12px;
  color: ${colors.text.secondary};
  margin-bottom: 4px;
`;

const SummaryValue = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: ${colors.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ChartsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const ChartCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
`;

const ChartTitle = styled.h3`
  font-size: 15px;
  font-weight: 600;
  color: ${colors.text.primary};
  margin: 0 0 16px 0;
`;

const TooltipBox = styled.div`
  background: white;
  border: 1px solid ${colors.neutral[200]};
  border-radius: ${colors.borderRadius.md};
  padding: 12px;
  box-shadow: ${colors.shadows.md};
`;

const TooltipTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${colors.text.primary};
  margin-bottom: 6px;
`;

const TooltipRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: ${colors.text.secondary};
  margin-top: 4px;
`;

const TooltipDot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
`;

const ContentCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  overflow: hidden;
`;

const TableContainer = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Thead = styled.thead`
  background: ${colors.neutral[50]};
`;

const Th = styled.th`
  padding: 12px 14px;
  text-align: ${props => props.align || 'left'};
  font-size: 11px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid ${colors.neutral[200]};
  white-space: nowrap;
`;

const Tbody = styled.tbody``;

const Tfoot = styled.tfoot``;

const Tr = styled.tr`
  border-bottom: 1px solid ${colors.neutral[100]};
  transition: background 0.15s;

  ${props => props.$isParent && `
    background: ${colors.neutral[50]};
    &:hover { background: ${colors.neutral[100]}; }
  `}

  ${props => !props.$isParent && `
    &:hover { background: ${colors.neutral[50]}; }
  `}

  ${props => props.$level === 1 && `
    background: ${colors.primary.main}06;
    border-bottom: 1px solid ${colors.neutral[200]};
  `}
`;

const Td = styled.td`
  padding: 10px 14px;
  font-size: 13px;
  color: ${colors.text.primary};
  text-align: ${props => props.align || 'left'};
`;

const AmountCell = styled(Td)`
  font-family: 'SF Mono', 'Consolas', 'Courier New', monospace;
  font-size: 13px;
  text-align: right;
  white-space: nowrap;

  ${props => props.$isParent && `
    font-weight: 600;
    font-size: 13px;
  `}

  ${props => props.$isTotal && `
    font-weight: 700;
    font-size: 14px;
    color: ${colors.text.primary};
  `}
`;

const AccountCode = styled.span`
  font-family: 'SF Mono', 'Consolas', 'Courier New', monospace;
  font-size: 12px;
  padding: 2px 6px;
  border-radius: 3px;
  white-space: nowrap;

  ${props => {
    const level = props.$level || 5;
    if (level <= 2) return `
      background: ${colors.primary.main}12;
      color: ${colors.primary.main};
      font-weight: 600;
    `;
    if (level <= 4) return `
      background: ${colors.neutral[100]};
      color: ${colors.text.secondary};
      font-weight: 500;
    `;
    return `
      background: ${colors.neutral[100]};
      color: ${colors.text.secondary};
    `;
  }}
`;

const AccountName = styled.span`
  font-size: 13px;
  color: ${colors.text.primary};
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding-left: ${props => props.$indent || 0}px;

  ${props => {
    const level = props.$level || 5;
    if (level <= 2) return `font-weight: 600; font-size: 14px;`;
    if (level <= 3) return `font-weight: 600; font-size: 13px;`;
    if (level <= 4) return `font-weight: 500;`;
    return '';
  }}
`;

const ClickableAccountName = styled.span`
  cursor: pointer;
  color: #1a73e8;
  &:hover {
    text-decoration: underline;
  }
`;

const AccountTypeBadge = styled.span`
  font-size: 10px;
  font-weight: 500;
  padding: 2px 6px;
  border-radius: 3px;
  background: ${colors.primary.main}10;
  color: ${colors.primary.main};
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const TotalRow = styled.tr`
  background: ${colors.neutral[100]};
  border-top: 2px solid ${colors.primary.main};

  td {
    padding: 14px;
  }
`;

const DifferenceRow = styled.tr`
  background: ${colors.error.main}08;
  border-top: 1px solid ${colors.error.main}30;

  td {
    padding: 10px 14px;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${colors.text.secondary};
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  color: ${colors.neutral[300]};
  margin-bottom: 16px;
`;

const EmptyText = styled.p`
  font-size: 16px;
  margin-bottom: 8px;
`;

export default TrialBalance;
